"use client"
import { SearchBar } from '@/components/ui';
import { Filter } from 'lucide-react';
import { useState } from 'react';

const mediaTypes = ['All', 'Images', 'Videos', 'Documents'];
const dateRanges = ['All Time', 'Today', 'This Week', 'This Month', 'Last 3 Months'];

interface MediaFiltersProps {
  onFiltersChange?: (filters: {
    searchTerm: string;
    selectedType: string;
    selectedDateRange: string;
  }) => void;
}

export default function MediaFilters({ onFiltersChange }: MediaFiltersProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('All');
  const [selectedDateRange, setSelectedDateRange] = useState('All Time');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    if (onFiltersChange) {
      onFiltersChange({
        searchTerm: value,
        selectedType,
        selectedDateRange
      });
    }
  };

  const handleTypeChange = (value: string) => {
    setSelectedType(value);
    if (onFiltersChange) {
      onFiltersChange({
        searchTerm,
        selectedType: value,
        selectedDateRange
      });
    }
  };

  const handleDateRangeChange = (value: string) => {
    setSelectedDateRange(value);
    if (onFiltersChange) {
      onFiltersChange({
        searchTerm,
        selectedType,
        selectedDateRange: value
      });
    }
  };

  const clearAllFilters = () => {
    setSelectedType('All');
    setSelectedDateRange('All Time');
    setSearchTerm('');
    if (onFiltersChange) {
      onFiltersChange({
        searchTerm: '',
        selectedType: 'All',
        selectedDateRange: 'All Time'
      });
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="space-y-4">
        {/* Main Search */}
        <div className="flex items-center space-x-4">
          <div className="flex-1 max-w-md">
            <SearchBar
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Search media by filename or blog title..."
            />
          </div>
          
          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <Filter className="h-4 w-4" />
            <span>Filters</span>
          </button>
        </div>

        {/* Advanced Filters */}
        {showAdvancedFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
            {/* Media Type Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Media Type
              </label>
              <select
                value={selectedType}
                onChange={(e) => handleTypeChange(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-primary focus:border-primary"
              >
                {mediaTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            {/* Date Range Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload Date
              </label>
              <select
                value={selectedDateRange}
                onChange={(e) => handleDateRangeChange(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-primary focus:border-primary"
              >
                {dateRanges.map(range => (
                  <option key={range} value={range}>{range}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Active Filters Display */}
        {(selectedType !== 'All' || selectedDateRange !== 'All Time' || searchTerm) && (
          <div className="flex items-center space-x-2 pt-4 border-t border-gray-200">
            <span className="text-sm text-gray-500">Active filters:</span>
            {searchTerm && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                Search: {searchTerm.substring(0, 20)}{searchTerm.length > 20 ? '...' : ''}
                <button
                  onClick={() => handleSearchChange('')}
                  className="ml-1 text-purple-600 hover:text-purple-800"
                >
                  ×
                </button>
              </span>
            )}
            {selectedType !== 'All' && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Type: {selectedType}
                <button
                  onClick={() => handleTypeChange('All')}
                  className="ml-1 text-blue-600 hover:text-blue-800"
                >
                  ×
                </button>
              </span>
            )}
            {selectedDateRange !== 'All Time' && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Date: {selectedDateRange}
                <button
                  onClick={() => handleDateRangeChange('All Time')}
                  className="ml-1 text-green-600 hover:text-green-800"
                >
                  ×
                </button>
              </span>
            )}
            <button
              onClick={clearAllFilters}
              className="text-sm text-red-600 hover:text-red-800"
            >
              Clear all
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
